import React, { createContext, ReactElement, useContext, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useActionToast } from '../services/actionToast';
import { api } from '../services/api';
import { getAuthStorage, removeAuthStorage, setAuthStorage } from '../services/storage';

interface UserImageType {
  large: string;
  medium: string;
  original: string;
  small: string;
}

interface UserType {
  id: number;
  cpf: string;
  cell_phone: string;
  email: string;
  genre: string;
  name: string;
  status: boolean;
  image: UserImageType | null;
}

interface ErrorType {
  status: string;
  msg: string;
}

interface AuthContextData {
  signed: boolean;
  user: UserType | object | any;
  loading: boolean;
  error: ErrorType | string | null | unknown;
  selectedInstitutionId: number | null;
  // eslint-disable-next-line no-unused-vars
  Login(user: object): Promise<void> | any;
  Logout(): void;
  SilentLogout(): void;
  getUser(): any;
  // eslint-disable-next-line no-unused-vars
  setSelectedInstitution(institutionId: number | null): void;
}

interface AuxProps {
  children: ReactElement | ReactElement[];
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<AuxProps> = ({ children }) => {
  const navigate = useNavigate();
  const toast = useActionToast();
  const [user, setUser] = useState<UserType | object | null>(null);
  const [error, setError] = useState<ErrorType | string | null | unknown>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<number | null>(null);

  useEffect(() => {
    const verifyLogged = async () => {
      if (user == null) {
        const storageAuth = await getAuthStorage();
        if (storageAuth) {
          api.defaults.headers.common.Authorization = `Bearer ${storageAuth.access_token}`;
          await getUser();
        }
      }
      setLoading(false);
    };
    verifyLogged();
  }, [user]);

  const getUser: AuthContextData['getUser'] = async () => {
    try {
      const res = await api.get('/v1/client');

      if (res.status !== 200) {
        toast({
          title: 'Erro ao carregar usuário',
          description: 'Não foi possível carregar suas informações.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top-right',
        });
        return false;
      }
      // set user
      setUser(res.data.data);
      return user;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to get user:', err);
      // Em caso de erro, limpar autenticação e redirecionar para login
      await removeAuthStorage();
      setUser(null);
      return false;
    }
  };

  const Login: AuthContextData['Login'] = async (data) => {
    try {
      setLoading(true);
      setError(null);

      api.defaults.headers.common.Authorization = process.env.REACT_APP_BASIC_ADMIN as string;
      const res = await api.post('/auth/token', {
        grant_type: 'password',
        ...data,
      });

      if (res.status === 200) {
        // save token in storage
        await setAuthStorage(res.data);

        // set token in header
        api.defaults.headers.common.Authorization = `Bearer ${res.data.access_token}`;

        // get user
        await getUser();

        setLoading(false);
        toast({
          title: 'Login concluído',
          description: 'Você entrou no painel administrativo.',
          status: 'success',
        });
        navigate('/', { replace: true });
      }
    } catch (e) {
      const err = e as AxiosError;

      setError(err.response?.data);
      setLoading(false);

      // Mostrar mensagem de erro ao usuário
      let errorMessage = 'Erro ao fazer login. Verifique sua conexão e tente novamente.';
      if (err.response?.status === 401) {
        errorMessage = 'Email ou senha incorretos';
      } else if (err.response?.status === 500) {
        errorMessage = 'Sistema temporariamente indisponível. Tente novamente em alguns instantes.';
      }

      toast({
        title: 'Erro ao fazer login',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  function clearSession() {
    removeAuthStorage();
    setUser(null);
    setSelectedInstitutionId(null);
  }

  function Logout() {
    clearSession();
    toast({
      title: 'Logout concluído',
      description: 'Você saiu do painel administrativo.',
      status: 'success',
    });
  }

  function SilentLogout() {
    clearSession();
  }

  const setSelectedInstitution = (institutionId: number | null) => {
    setSelectedInstitutionId(institutionId);
    // Optionally save to localStorage for persistence
    if (institutionId) {
      localStorage.setItem('selectedInstitutionId', institutionId.toString());
    } else {
      localStorage.removeItem('selectedInstitutionId');
    }
  };

  // Load selected institution from localStorage on mount
  useEffect(() => {
    const savedInstitutionId = localStorage.getItem('selectedInstitutionId');
    if (savedInstitutionId) {
      setSelectedInstitutionId(parseInt(savedInstitutionId, 10));
    }
  }, []);

  return (
    <AuthContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        signed: Boolean(user),
        user,
        loading,
        error,
        selectedInstitutionId,
        Login,
        Logout,
        SilentLogout,
        getUser,
        setSelectedInstitution,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
}
